import React, {useMemo, useState} from "react";
import {
    Box, TextField, MenuItem, Checkbox, ListSubheader, Typography,
    Grid, Slider, FormControl, InputLabel, FormHelperText,
    Accordion, AccordionSummary, AccordionDetails, IconButton, Paper
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {inputTools} from "../InputTools/constant.js";
import {isMobile, parseJSON} from "../App/utils.js";
import {KBName} from "../names";
import {getGroupedModels} from "./Chat";
import {colorGreen} from "../App/theme";
import {Bolt, FlashOn, Speed} from "@mui/icons-material";
import {ChatModels} from "../constants";
import DatabaseSearchSettings from "./DatabaseSearchSettings";

export const renderImageGenerationModelsSelect = ({
                                                      groupedModels,
                                                      onChange,
                                                      model,
                                                      details = true,
                                                      noneOption,
                                                      style = {},
                                                      variant = "outlined",
                                                      withHeaders = true,
                                                  }) => {
    const formatPrice = (price) => {
        return `$${(price / 100000).toFixed(3)}`;
    };

    const renderValue = (selected) => {
        const selectedModel = Object.values(groupedModels)
            .flat()
            .find((m) => m.id === selected);
        let label = selectedModel ? (selectedModel.shortName || selectedModel.name) : noneOption || "";

        return (
            <Box position="relative" display="inline-block" width="100%">
                <Typography variant="body1"
                            align={!isMobile ? "left" : undefined}
                            pl={!isMobile ? 2 : 0}
                >{label}</Typography>
            </Box>
        );
    };

    return (
        <Box mt={2} sx={{ width: "100%", ...style }}>
            <TextField
                select
                value={model}
                onChange={(e) => {
                    const modelId = e.target.value;
                    let vendor = null;

                    Object.values(groupedModels).flat().forEach(model => {
                        if (model.id === modelId) {
                            vendor = model.vendor;
                        }
                    });

                    onChange(e.target.value, vendor);
                }}
                fullWidth
                variant={variant}
                label={details ? "Image Generation Model" : undefined}
                InputProps={
                    variant === "outlined"
                        ? {}
                        : {
                            disableUnderline: true,
                            style: { padding: 0 },
                        }
                }
                SelectProps={{
                    renderValue,
                    IconComponent: () => null
                }}
            >
                {withHeaders && (
                    <ListSubheader style={{ paddingBottom: "8px", minWidth: '350px' }} key={"first"}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                                <Typography variant="body2">Model</Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <Typography variant="body2">Price per Request</Typography>
                            </Grid>
                            {details && (
                                <Grid item xs={1}>
                                    <Typography variant="body2">Service</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </ListSubheader>
                )}

                {noneOption && (
                    <MenuItem value={""} key={"none"}>
                        <Typography variant="body1">{noneOption}</Typography>
                    </MenuItem>
                )}

                {Object.entries(groupedModels).map(
                    ([group, models]) => [
                        <ListSubheader key={group}>{group}</ListSubheader>,
                        models.map((myModel) => (
                            <MenuItem value={myModel.id} key={myModel.id}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6} style={{ marginLeft: 6, marginRight: 6 }}>
                                        <Typography variant="body2">{myModel.shortName}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="body2">
                                            {formatPrice(myModel.pricePerRequest)}
                                        </Typography>
                                    </Grid>
                                    {details && (
                                        <Grid item xs={1}>
                                            <Typography variant="body2">
                                                {myModel.serviceDescription}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </MenuItem>
                        )),
                    ]
                )}
            </TextField>
        </Box>
    );
};

export const renderChatModelsSelect = ({
                                           withoutObsolete,
                                           groupedModels,
                                           onChange,
                                           model,
                                           details = true,
                                           noneOption,
                                           style = {},
                                           variant = "outlined",
                                           withHeaders = true,
                                       }) => {
    const formatPrice = (price, n = 1) => {
        return `${(price / (1000)).toFixed(n)}`;
    };
    const formatContextSize = (size) => `${size / 1000}K`;
    const precision = (size) => {
        if (size === "Large") return 3;
        if (size === "Medium") return 4;
        return 5;
    };

    const renderValue = (selected) => {
        const selectedModel = Object.values(groupedModels)
            .flat()
            .find((m) => m.id === selected);
        let label = selectedModel ? selectedModel.name : noneOption || "";

        return (
            <Box position="relative" display="inline-block" width="100%">
                <Typography
                    align={!isMobile ? "left" : undefined}
                    pl={!isMobile ? 2 : 0}
                    variant="body1">{label}</Typography>
                {selectedModel?.isOnPremises && (
                    <Typography
                        variant="caption"
                        color={colorGreen}
                        position="absolute"
                        bottom={-6}
                        right={-20}
                        style={{ fontSize: '0.45rem' }}
                    >
                        On-Premises
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box mt={2} sx={{ width: "100%", ...style }}>
            <TextField
                select
                value={model}
                onChange={(e) => {
                    const modelId = e.target.value;
                    let vendor = null;

                    // Iterate over all sizes dynamically
                    Object.keys(groupedModels).forEach(size => {
                        const models = groupedModels[size];
                        const model = models.find(m => m.id === modelId);
                        if (model) {
                            vendor = model.vendor;
                        }
                    });

                    onChange(e.target.value, vendor);
                }}
                fullWidth
                variant={variant}
                label={details ? "Chat Model" : undefined}
                InputProps={
                    variant === "outlined"
                        ? {}
                        : {
                            disableUnderline: true,
                            style: { padding: 0 },
                        }
                }
                SelectProps={{
                    renderValue,
                    IconComponent: () => null
                }}
            >
                {withHeaders && (
                    <ListSubheader style={{ paddingBottom: "8px" }} key={"first"}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={5}>
                                <Typography variant="body2">Model</Typography>
                            </Grid>

                            <>
                                {!details ? (
                                    <>
                                        <Grid item xs={2}>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" style={{ textAlign: 'right', paddingRight: '10px' }}>input / output</Typography>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={5}>
                                            <Typography variant="body2">input / output</Typography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Typography variant="body2">Max Context</Typography>
                                        </Grid>
                                    </>
                                )}
                            </>
                        </Grid>
                    </ListSubheader>
                )}

                {noneOption && (
                    <MenuItem value={""} key={"none"}>
                        <Typography variant="body1">{noneOption}</Typography>
                    </MenuItem>
                )}

                {Object.entries(groupedModels).map(
                    ([size, models]) =>
                        (size !== "Obsolete" || !withoutObsolete) && [
                            <ListSubheader key={size}>{size}</ListSubheader>,
                            models
                                .filter((model) => !model?.obsolete || !withoutObsolete)
                                .filter((model) => !model?.hidden)
                                .map((myModel) => (
                                    <MenuItem value={myModel.id} key={myModel.id}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid
                                                item
                                                xs={5}
                                                style={{ marginLeft: 0, marginRight: 20 }}
                                            >
                                                <Box style={{ position: 'relative' }}>
                                                    {myModel.instant && (
                                                        <Typography 
                                                            component="div" 
                                                            style={{ 
                                                                fontSize: '0.5rem',
                                                                color: '#999',
                                                                marginBottom: -2,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Bolt style={{fontSize: 9, marginRight: 2}} />
                                                            {myModel.instant}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2">
                                                        {myModel.name}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <>
                                                {!details ? (
                                                    <>
                                                        <Grid item xs={2}>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body2" style={{ textAlign: 'right', paddingRight: '10px' }}>
                                                                {myModel.inputPrice
                                                                    ? `${formatPrice(
                                                                        myModel.inputPrice,
                                                                        2
                                                                    )} c / ${formatPrice(
                                                                        myModel.outputPrice,
                                                                        2
                                                                    )} c`
                                                                    : "On-premises model"}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Grid item xs={5}>
                                                            <Typography variant="body2">
                                                                {myModel.inputPrice
                                                                    ? `${formatPrice(
                                                                        myModel.inputPrice,
                                                                        2
                                                                    )} c / ${formatPrice(
                                                                        myModel.outputPrice,
                                                                        2
                                                                    )} c`
                                                                    : "On-premises model"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={1}>
                                                            <Typography variant="body2">
                                                                {formatContextSize(myModel.context)}
                                                            </Typography>
                                                        </Grid>
                                                    </>
                                                )}
                                            </>
                                        </Grid>
                                    </MenuItem>
                                )),
                        ]
                )}
            </TextField>
        </Box>
    );
};

const ChatSettings = ({
    KBData,
    setKBData,
    network,
    searchEngine,
    setSearchEngine,
    embeddingModel,
    setEmbeddingModel,
    embeddingDimension,
    setEmbeddingDimension,
    embeddingConfigured,
    setSearchSettingsHasChanges
}) => {
    const groupedModels = useMemo(getGroupedModels, []);
    const [advancedExpanded, setAdvancedExpanded] = useState(false);
    const [instructionsExpanded, setInstructionsExpanded] = useState(false);
    const [toolsExpanded, setToolsExpanded] = useState(false);
    
    // Add CSS animation for Cerebras badge
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes pulse {
                0% {
                    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7);
                    transform: scale(1);
                }
                50% {
                    box-shadow: 0 0 0 6px rgba(255, 107, 107, 0);
                    transform: scale(1.05);
                }
                100% {
                    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0);
                    transform: scale(1);
                }
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const formatPrice = (price, n = 5) => `$${(price / (100*1000)).toFixed(n)}`;

    const hasImageEditor = KBData?.inputTools?.map(o => parseJSON(o))?.some(o => o?.type === 'imageEditor')

    const handleChangeInputTools = (event) => {

        let inputToolsPerType = event.target.value.reduce((map, item) => {
            const type = parseJSON(item)?.type || inputTools?.[item]?.type || item;

            const existingItems = map.get(type) || [];
            if (type === "imageEditor") {
                // allow multiple tools per type (able to select more than one)
                return map.set(type, [...existingItems, item]);
            } else {
                // ensure unique tools per type (unable to select more than one of this type)
                return map.set(type, [item]);
            }
        }, new Map());

        let multipleInputToolsPerType = Array.from(inputToolsPerType.values()).flat();

        setKBData({
            ...KBData,
            inputTools: multipleInputToolsPerType
        });
    };

    // Get max thinking tokens for the selected model
    const maxThinkingTokens = ChatModels?.[KBData?.model]?.maxThinkingTokens;

    // Handler for the thinking tokens slider
    const handleThinkingTokensChange = (event, newValue) => {
        setKBData({
            ...KBData,
            options: {
                ...KBData.options,
                maxThinkingTokens: newValue
            }
        });
    };

    return (
        <Box mt={1}>
            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <TextField
                    inputProps={{
                        autoComplete: 'kb-title',
                        form: {
                            autoComplete: 'off',
                        },
                    }}
                    label={KBName + " Title"}
                    value={KBData.kbTitle}
                    onChange={(e) => setKBData({
                        ...KBData,
                        kbTitle: e.target.value
                    })}
                    fullWidth
                    required
                    variant="outlined"
                />
            </Box>

            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <Box 
                    sx={{ 
                        position: 'relative',
                        ...(instructionsExpanded && {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1300,
                            backgroundColor: 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 2
                        })
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ flexGrow: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                            Base Instructions
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setInstructionsExpanded(!instructionsExpanded)}
                            sx={{ ml: 1 }}
                        >
                            {instructionsExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Box>
                    <TextField
                        value={KBData.kbInstructions}
                        onChange={(e) => setKBData({
                            ...KBData,
                            kbInstructions: e.target.value
                        })}
                        fullWidth
                        multiline
                        minRows={instructionsExpanded ? undefined : 4}
                        maxRows={instructionsExpanded ? undefined : 12}
                        variant="outlined"
                        placeholder="Enter detailed instructions for the AI assistant..."
                        sx={{
                            ...(instructionsExpanded && {
                                flex: 1,
                                '& .MuiInputBase-root': {
                                    height: 'calc(100vh - 120px)',
                                    alignItems: 'flex-start'
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    height: '100% !important',
                                    overflow: 'auto !important'
                                }
                            }),
                            ...(!instructionsExpanded && {
                                '& .MuiInputBase-input': {
                                    fontSize: '13px',
                                    lineHeight: '1.4'
                                }
                            })
                        }}
                    />
                </Box>
            </Box>

            {KBData?.kbTitle && renderChatModelsSelect({
                groupedModels,
                onChange: (model) => setKBData({ ...KBData, model}),
                model: KBData?.model
            })}

            <DatabaseSearchSettings
                KBData={KBData}
                setKBData={setKBData}
                searchEngine={searchEngine}
                setSearchEngine={setSearchEngine}
                embeddingModel={embeddingModel}
                setEmbeddingModel={setEmbeddingModel}
                embeddingDimension={embeddingDimension}
                setEmbeddingDimension={setEmbeddingDimension}
                embeddingConfigured={embeddingConfigured}
                setSearchSettingsHasChanges={setSearchSettingsHasChanges}
            />

            {/* Advanced Settings Accordion */}
            <Accordion
                expanded={advancedExpanded}
                onChange={() => setAdvancedExpanded(!advancedExpanded)}
                sx={{ mt: 3, mb: 2 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="advanced-settings-content"
                    id="advanced-settings-header"
                >
                    <Typography variant="subtitle1">Limits</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {/* Thinking Tokens Slider - only show if maxThinkingTokens is defined for this model */}
                    {maxThinkingTokens && (
                        <Box sx={{ width: '100%', mb: 3 }}>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    Thinking Tokens: {KBData?.options?.maxThinkingTokens || 0}
                                </Typography>
                                <Slider
                                    value={KBData?.options?.maxThinkingTokens || 0}
                                    onChange={handleThinkingTokensChange}
                                    min={0}
                                    max={maxThinkingTokens}
                                    valueLabelDisplay="auto"
                                    aria-labelledby="thinking-tokens-slider"
                                />
                                <FormHelperText>
                                    Maximum tokens for AI thinking/reasoning before responding
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    )}

                    {/* Messages Window Slider */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography gutterBottom>
                                Messages Window: {KBData?.options?.messagesWindow || 0}
                            </Typography>
                            <Slider
                                value={KBData?.options?.messagesWindow || 0}
                                onChange={(event, newValue) => setKBData({
                                    ...KBData,
                                    options: {
                                        ...KBData.options,
                                        messagesWindow: newValue
                                    }
                                })}
                                min={0}
                                max={100}
                                valueLabelDisplay="auto"
                                aria-labelledby="messages-window-slider"
                            />
                            <FormHelperText>
                                Maximum recent messages to retain in conversation (0 = no limit)
                            </FormHelperText>
                        </FormControl>
                    </Box>

                    {/* Tokens Window Slider */}
                    <Box sx={{ width: '100%' }}>
                        <FormControl fullWidth>
                            <Typography gutterBottom>
                                Tokens Window: {KBData?.options?.tokensWindow || 0}
                            </Typography>
                            <Slider
                                value={KBData?.options?.tokensWindow || 0}
                                onChange={(event, newValue) => setKBData({
                                    ...KBData,
                                    options: {
                                        ...KBData.options,
                                        tokensWindow: newValue
                                    }
                                })}
                                min={0}
                                max={200000}
                                step={1000}
                                valueLabelDisplay="auto"
                                aria-labelledby="tokens-window-slider"
                            />
                            <FormHelperText>
                                Maximum tokens to retain from recent messages (0 = no limit)
                            </FormHelperText>
                        </FormControl>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={toolsExpanded}
                onChange={() => setToolsExpanded(!toolsExpanded)}
                sx={{ mt: 2 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="tools-content"
                    id="tools-header"
                >
                    <Typography variant="subtitle1">Tools</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.keys(inputTools).filter(key => !inputTools[key]?.hidden && key !== 'imageEditor').map(key => {
                            const tool = inputTools[key];
                            const serviceName = tool?.service?.name;
                            const pricePerRequest = tool?.service?.pricePerRequest;
                            const pricePerMinute = tool?.service?.pricePerMinute;

                            const toolValue = key;
                            const isSelected = KBData?.inputTools?.indexOf(key) > -1;

                            const icon = tool.icon();

                            let title = serviceName
                                ? (isMobile ? tool?.desc : serviceName + ' - ' + tool?.desc)
                                : tool.settingsLabel || tool.label;

                            const label = title;

                            let priceText = (pricePerRequest ? `${formatPrice(pricePerRequest, 2)}${!isMobile ? ' c/request' : ' c/req'}` : '') +
                                (pricePerMinute ? `${formatPrice(pricePerMinute, 2)}${!isMobile ? ' c/minute' : ' c/min'}` : '');

                            const handleToggleTool = () => {
                                const currentTools = KBData.inputTools || [];
                                let newTools;

                                if (isSelected) {
                                    // Remove tool
                                    newTools = currentTools.filter(t => t !== key);
                                } else {
                                    // Add tool
                                    newTools = [...currentTools, toolValue];
                                }

                                // Apply the same logic as handleChangeInputTools
                                let inputToolsPerType = newTools.reduce((map, item) => {
                                    const type = parseJSON(item)?.type || inputTools?.[item]?.type || item;
                                    const existingItems = map.get(type) || [];
                                    // Note: imageEditor would allow multiple, but we're hiding it from UI
                                    if (type === "imageEditor") {
                                        return map.set(type, [...existingItems, item]);
                                    } else {
                                        return map.set(type, [item]);
                                    }
                                }, new Map());

                                let multipleInputToolsPerType = Array.from(inputToolsPerType.values()).flat();

                                setKBData({
                                    ...KBData,
                                    inputTools: multipleInputToolsPerType
                                });
                            };

                            return (
                                <Paper
                                    key={key}
                                    sx={{
                                        p: 2,
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                                        border: '1px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            backgroundColor: isSelected ? 'action.selected' : 'action.hover'
                                        }
                                    }}
                                    onClick={handleToggleTool}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                opacity: isSelected ? 1 : 0.6,
                                                transition: 'opacity 0.2s'
                                            }}>
                                                {React.cloneElement(icon, {
                                                    style: {
                                                        ...icon.props.style,
                                                        fontSize: '24px'
                                                    }
                                                })}
                                            </Box>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                                    {label}
                                                </Typography>
                                                {/*{priceText && (*/}
                                                {/*    <Typography variant="caption" color="text.secondary">*/}
                                                {/*        {priceText}*/}
                                                {/*    </Typography>*/}
                                                {/*)}*/}
                                            </Box>
                                        </Box>
                                        <Checkbox
                                            checked={isSelected}
                                            sx={{
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 28
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </AccordionDetails>
            </Accordion>

        </Box>
    );
};

export default ChatSettings;